/**
 * Emoji no editor (H16c).
 *
 * ── Por que uma lista escolhida, e não uma biblioteca ─────────────────────
 *
 * As bibliotecas de emoji passam de 1 MB — elas carregam os ~3.800 do padrão
 * Unicode, com nomes, palavras-chave e variações de tom de pele em vários
 * idiomas. Isso entraria no pacote que TODA pessoa baixa ao abrir o
 * atendimento, para servir a um campo de texto.
 *
 * O que uma conversa de venda de carro usa cabe aqui. Se um dia faltar, a lista
 * cresce — e continua sendo dado, não dependência.
 *
 * ── Os recentes são o que realmente serve ─────────────────────────────────
 *
 * Ninguém navega oito categorias para mandar 👍. Quem responde cliente usa os
 * mesmos seis ou sete o dia inteiro, e é isso que a primeira fileira mostra.
 */

/** Onde os recentes ficam. Por navegador, e sem passar pelo servidor. */
const CHAVE_DOS_RECENTES = 'cliqcrm:emojis-recentes';

/** Quantos recentes lembrar. Uma fileira — mais que isso vira outra lista. */
const QUANTOS_RECENTES = 8;

export interface GrupoDeEmoji {
  readonly nome: string;
  readonly itens: readonly string[];
}

/**
 * A lista, agrupada como quem procura pensa.
 *
 * A ordem dos grupos é de frequência no atendimento, não a do Unicode:
 * reação vem primeiro porque é o que mais se manda; bandeira não está aqui
 * porque nunca apareceu numa conversa de revenda.
 */
export const GRUPOS_DE_EMOJI: readonly GrupoDeEmoji[] = [
  {
    nome: 'Reações',
    itens: [
      '👍',
      '👏',
      '🙏',
      '💪',
      '🤝',
      '👌',
      '🙌',
      '✌️',
      '❤️',
      '🧡',
      '💚',
      '💙',
      '💜',
      '🔥',
      '⭐',
      '✨',
      '✅',
      '❌',
      '⚠️',
      '❗',
      '❓',
      '💯',
      '🎉',
      '🎊',
    ],
  },
  {
    nome: 'Rostos',
    itens: [
      '😀',
      '😃',
      '😄',
      '😁',
      '😊',
      '🙂',
      '😉',
      '😍',
      '😘',
      '😗',
      '🤗',
      '🤩',
      '🥳',
      '😎',
      '🤓',
      '🧐',
      '🙃',
      '😅',
      '😂',
      '🤣',
      '😇',
      '🥰',
      '😴',
      '🤔',
      '😐',
      '😑',
      '😬',
      '🙄',
      '😳',
      '😔',
      '😢',
      '😭',
      '😤',
      '😠',
      '🥺',
      '😱',
      '🤯',
      '🤦',
      '🤷',
      '😷',
    ],
  },
  {
    nome: 'Veículos',
    itens: [
      '🚗',
      '🚙',
      '🚕',
      '🚐',
      '🛻',
      '🚚',
      '🚛',
      '🏎️',
      '🏍️',
      '🛵',
      '🚲',
      '🚜',
      '🚑',
      '🚓',
      '🚒',
      '⛽',
      '🔧',
      '🔩',
      '🛞',
      '🧰',
      '🚦',
      '🛣️',
      '🅿️',
      '🚧',
    ],
  },
  {
    nome: 'Negócio',
    itens: [
      '💰',
      '💵',
      '💳',
      '🧾',
      '📄',
      '📋',
      '📊',
      '📈',
      '📉',
      '🤑',
      '🏦',
      '🔑',
      '🗝️',
      '📝',
      '✍️',
      '🖊️',
      '📌',
      '📎',
      '🗂️',
      '📁',
      '🏷️',
      '🛒',
      '🎁',
      '🤞',
    ],
  },
  {
    nome: 'Tempo e lugar',
    itens: [
      '📅',
      '🗓️',
      '⏰',
      '⏳',
      '🕐',
      '📍',
      '🗺️',
      '🏠',
      '🏢',
      '🏬',
      '🚩',
      '🌎',
      '☀️',
      '🌧️',
      '❄️',
      '🌙',
    ],
  },
  {
    nome: 'Contato',
    itens: [
      '📱',
      '☎️',
      '📞',
      '📲',
      '💬',
      '💭',
      '📧',
      '✉️',
      '📨',
      '📩',
      '🔔',
      '🔕',
      '📢',
      '📣',
      '👋',
      '👀',
    ],
  },
];

/** Todos, sem grupo — para conferir se um recente ainda existe na lista. */
const TODOS = new Set(GRUPOS_DE_EMOJI.flatMap((grupo) => grupo.itens));

/**
 * Os recentes guardados, filtrados pelo que ainda existe.
 *
 * O filtro não é paranoia: a lista acima muda com o tempo, e um recente que
 * saiu dela apareceria como um quadradinho vazio — o pior tipo de defeito
 * visual, porque parece fonte quebrada e não dado velho.
 *
 * Falha de leitura devolve lista vazia. `localStorage` estoura em navegação
 * anônima de alguns navegadores, e um seletor de emoji não pode derrubar a
 * tela de atendimento por causa disso.
 */
export function lerRecentes(
  deposito: Storage | undefined = globalThis.localStorage,
): readonly string[] {
  try {
    const bruto = deposito?.getItem(CHAVE_DOS_RECENTES);
    if (!bruto) return [];
    const lidos: unknown = JSON.parse(bruto);
    if (!Array.isArray(lidos)) return [];
    return lidos.filter((item): item is string => typeof item === 'string' && TODOS.has(item));
  } catch {
    return [];
  }
}

/**
 * Registra um uso e devolve a lista nova.
 *
 * O usado vai para a frente e não duplica: quem acabou de mandar 👍 vai
 * mandar de novo, e empurrá-lo para o fim da fileira faria a lista de
 * "recentes" ordenar por primeira vez em vez de por última.
 */
export function registrarUso(
  emoji: string,
  deposito: Storage | undefined = globalThis.localStorage,
): readonly string[] {
  const nova = [emoji, ...lerRecentes(deposito).filter((item) => item !== emoji)].slice(
    0,
    QUANTOS_RECENTES,
  );
  try {
    deposito?.setItem(CHAVE_DOS_RECENTES, JSON.stringify(nova));
  } catch {
    // Cota cheia ou armazenamento bloqueado. O emoji já foi inserido no texto;
    // não lembrar dele é uma comodidade perdida, não um erro para mostrar.
  }
  return nova;
}

export interface TextoComCursor {
  readonly texto: string;
  readonly cursor: number;
}

/**
 * Insere no ponto onde o cursor está, substituindo o que estiver selecionado.
 *
 * ── Por que não concatenar no fim ─────────────────────────────────────────
 *
 * Porque quem escreve "obrigado pela " e quer o emoji no meio da frase não
 * pretende que ele vá para depois do ponto final. Concatenar é o que quase
 * todo seletor caseiro faz, e o efeito é a pessoa clicar, ver o emoji no lugar
 * errado, e passar a recortar e colar.
 *
 * O cursor volta LOGO DEPOIS do que foi inserido, para dar para continuar
 * digitando sem tocar no mouse.
 */
export function inserirNoCursor(
  texto: string,
  inicio: number,
  fim: number,
  trecho: string,
): TextoComCursor {
  // Limites defensivos: `selectionStart` é nulo em alguns navegadores quando o
  // campo nunca recebeu foco, e o `null` vira `0` — o que jogaria o emoji para
  // o começo do texto sem nenhum aviso.
  const de = Math.max(0, Math.min(inicio, texto.length));
  const ate = Math.max(de, Math.min(fim, texto.length));

  return {
    texto: texto.slice(0, de) + trecho + texto.slice(ate),
    cursor: de + trecho.length,
  };
}
